import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Container,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import Logout from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { OrganizationAddressStep } from '@gastrosoft/shared/ui';
import { useAuth } from '@/app/auth/AuthContext';
import { useSetup } from '@/app/contexts/SetupContext';
import { useOrganization } from '@/app/contexts/OrganizationContext';
import { useNotification } from '@/app/contexts/NotificationContext';
import { executeSetup } from './services/executeSetup';
import { hasSetupData, getAllSetupData } from './utils/setupStorage';
import SetupLoadingScreen from './components/SetupLoadingScreen';
import OrganizationStep from './steps/OrganizationStep';
import CustomerContactStep from './steps/CustomerContactStep';

const STEP_LABEL_KEYS = ['Organization', 'OrganizationAddress', 'setup.customerContact.stepLabel'];

const SetupWorkflow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { setNeedsSetup, resetSetup } = useSetup();
  const { showError } = useNotification();
  const { organizations, loading: organizationsLoading, refetchOrganizations } = useOrganization();

  const [activeStep, setActiveStep] = useState(0);
  const [checkingProgress, setCheckingProgress] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const isMobile = useMediaQuery('(max-width: 800px)');

  // Check if setup is needed and restore step from localStorage
  useEffect(() => {
    if (organizationsLoading || !checkingProgress) return;

    if (organizations.length > 0) {
      // handleComplete sets isExecuting, then refetchOrganizations() updates this list before
      // navigate() finishes. Navigating here would unmount this tree mid-flight.
      if (isExecuting) {
        return;
      }
      setNeedsSetup(false);
      navigate('/dashboard', { replace: true });
      return;
    }

    if (hasSetupData()) {
      const setupData = getAllSetupData();

      if (setupData.customerContact) {
        setActiveStep(2);
      } else if (setupData.organizationAddress) {
        setActiveStep(1);
      } else if (setupData.organization) {
        setActiveStep(0);
      } else {
        setActiveStep(0);
      }
    } else {
      setActiveStep(0);
    }

    setCheckingProgress(false);
  }, [organizations, organizationsLoading, checkingProgress, isExecuting, setNeedsSetup, navigate]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = async () => {
    setIsExecuting(true);
    setExecutionProgress(0);
    setCurrentStep('Preparing');

    try {
      await executeSetup((progress, step) => {
        setExecutionProgress(progress);
        setCurrentStep(step);
      });

      await refetchOrganizations();
      resetSetup();
      setNeedsSetup(false);
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      setIsExecuting(false);
      showError(error instanceof Error ? error.message : t('errors.generic'));
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <OrganizationStep onNext={handleNext} />;
      case 1:
        return (
          <OrganizationAddressStep
            onNext={handleNext}
            onBack={handleBack}
            localStorageKey="organizationAddress"
          />
        );
      case 2:
        return <CustomerContactStep onNext={handleComplete} onBack={handleBack} />;
      default:
        return null;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isExecuting) {
    return <SetupLoadingScreen progress={executionProgress} currentStep={currentStep} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<Logout />}
        onClick={handleLogout}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: (theme) => theme.zIndex.tooltip,
          bgcolor: 'background.paper',
          borderColor: 'divider',
          color: 'text.primary',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'divider',
          },
        }}
      >
        {t('common.logout')}
      </Button>
      <Container maxWidth="md" sx={{ py: isMobile ? 0 : 4 }}>
        <Paper sx={{ p: isMobile ? 2 : 4 }}>
          <Typography variant="h4" sx={{ mb: 1, textAlign: 'center' }}>
            {t('setup.welcomeTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            {t('setup.welcomeDescription')}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
            {STEP_LABEL_KEYS.map((labelKey) => (
              <Step key={labelKey} sx={{ width: '33%' }}>
                <StepLabel>{!isMobile ? t(labelKey) : undefined}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 4 }}>
            {checkingProgress ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              renderStepContent(activeStep)
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SetupWorkflow;
